import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { type ApolloCache } from '@apollo/client';
import { useMutation } from '@apollo/client/react';

import { gql } from '@graphql/__generated__';
import {
  base64PngToJpegFile,
  useEnhanceImage,
} from '@modules/_shared/components/image-gallery/enhance/enhance-image-utils';
import { useEnhanceProfileImageActions } from '@modules/_shared/components/image-gallery/enhance/enhance-profile-actions';
import { EnhanceImagePanel } from '@modules/_shared/components/image-gallery/enhance/EnhanceImagePanel';
import { GET_USER_IMAGES } from '@modules/_shared/components/image-gallery/imageGalleryQueries';
import type { ImageGalleryItem } from '@modules/_shared/components/image-gallery/NewImageGallery';
import { ImageGalleryDialog } from '@modules/_shared/components/image-upload/components/ImageGalleryDialog';
import { ImageUpload } from '@modules/_shared/components/image-upload/components/ImageUpload';
import { isCurrentImageUnchanged } from '@modules/_shared/components/image-upload/imageSourceUtils';
import { processImage } from '@modules/_shared/components/image-upload/processors/imageProcessor';
import type {
  ImageSource,
  ImageTransform,
} from '@modules/_shared/components/image-upload/types';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { GET_TEAM_MEMBER_EDIT_DETAILS } from '@modules/_shared/components/visibility-drawer/queries/agent-queries';
import {
  uploadFileAsync,
  useFileUpload,
} from '@modules/_shared/utils/files/file-upload';
import { useLocale } from '@shared/hooks/locale';

const INSERT_USER_IMAGE_ATOMIC = gql(/* GraphQL */ `
  mutation InsertUserImageAtomic(
    $url: String!
    $userId: uuid!
    $setPrimary: Boolean!
    $aiEnhancedAt: timestamptz
  ) {
    update_user_images(
      where: { user_id: { _eq: $userId }, is_primary: { _eq: true } }
      _set: { is_primary: false }
    ) @include(if: $setPrimary) {
      affected_rows
    }

    insert_user_images_one(
      object: {
        user_id: $userId
        image: { data: { url: $url, ai_enhanced_at: $aiEnhancedAt } }
        is_primary: $setPrimary
      }
    ) {
      id
      is_primary
      image {
        id
        url
      }
    }
  }
`);

export const SET_PRIMARY_IMAGE = gql(/* GraphQL */ `
  mutation SetPrimaryImage($userId: uuid!, $userImageId: uuid!) {
    update_user_images(
      where: { user_id: { _eq: $userId }, is_primary: { _eq: true } }
      _set: { is_primary: false }
    ) {
      affected_rows
    }

    update_user_images_by_pk(
      pk_columns: { id: $userImageId }
      _set: { is_primary: true }
    ) {
      id
      is_primary
      image {
        id
        url
      }
    }
  }
`);

type AgentImageUploadModalProps = {
  userId: string;
  currentImageUrl?: string;
  open: boolean;
  onClose: () => void;
  /** Open the AI enhance panel immediately, sourced from currentImageUrl. */
  autoOpenEnhance?: boolean;
};

const shouldOnlySetPrimary = (
  source: ImageSource,
  transform: ImageTransform,
): boolean => {
  const hasNoTransformations = Object.keys(transform).length === 0;
  return Boolean(
    source.metadata?.galleryId &&
      source.metadata?.userImageId &&
      hasNoTransformations,
  );
};

export const updateCacheForPrimaryImage = (
  cache: ApolloCache,
  userId: string,
  newPrimaryImage: { id: string; image: { id: string; url?: string | null } },
) => {
  const galleryData = cache.readQuery({
    query: GET_USER_IMAGES,
    variables: { user_id: userId },
  });

  if (galleryData?.user_images) {
    cache.writeQuery({
      query: GET_USER_IMAGES,
      variables: { user_id: userId },
      data: {
        user_images: galleryData.user_images.map(image => ({
          ...image,
          is_primary: image.id === newPrimaryImage.id,
        })),
      },
    });
  }

  const visibilityData = cache.readQuery({
    query: GET_TEAM_MEMBER_EDIT_DETAILS,
    variables: { userId },
  });

  if (visibilityData?.users_by_pk) {
    cache.writeQuery({
      query: GET_TEAM_MEMBER_EDIT_DETAILS,
      variables: { userId },
      data: {
        ...visibilityData,
        users_by_pk: {
          ...visibilityData.users_by_pk,
          user_images: [
            {
              id: newPrimaryImage.id,
              image: newPrimaryImage.image,
            },
          ],
        },
      },
    });
  }
};

export const AgentImageUploadModal = memo(
  ({
    userId,
    currentImageUrl,
    open,
    onClose,
    autoOpenEnhance,
  }: AgentImageUploadModalProps) => {
    const { t } = useLocale();

    const [isUploading, setIsUploading] = useState(false);
    const [showGallery, setShowGallery] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [enhancePanelOpen, setEnhancePanelOpen] = useState(false);

    const { categories, subcategories, actions } =
      useEnhanceProfileImageActions();
    const handleEnhance = useEnhanceImage({
      aspectRatio: '1:1',
      imageSize: '1K',
    });

    const [selectedSource, setSelectedSource] = useState<ImageSource | null>(
      null,
    );

    const defaultSource = useMemo(
      () =>
        currentImageUrl
          ? {
              type: 'url' as const,
              data: currentImageUrl,
              metadata: { isCurrentImage: true },
            }
          : null,
      [currentImageUrl],
    );

    useEffect(() => {
      if (open && !selectedSource && defaultSource) {
        setSelectedSource(defaultSource);
      }
    }, [open, selectedSource, defaultSource]);

    useEffect(() => {
      if (open && !currentImageUrl) {
        setShowGallery(true);
      }
    }, [open, currentImageUrl]);

    useEffect(() => {
      if (open && autoOpenEnhance) {
        setEnhancePanelOpen(true);
      }
    }, [open, autoOpenEnhance]);

    const [uploadFile] = useFileUpload('image');
    const [insertUserImageAtomic] = useMutation(INSERT_USER_IMAGE_ATOMIC, {
      update: (cache, { data }) => {
        if (
          !data?.insert_user_images_one ||
          !data.insert_user_images_one.is_primary
        ) {
          return;
        }

        const newPrimaryImage = data.insert_user_images_one;

        const galleryData = cache.readQuery({
          query: GET_USER_IMAGES,
          variables: { user_id: userId },
        });

        if (galleryData?.user_images) {
          const existingImages = galleryData.user_images;
          const imageAlreadyInCache = existingImages.some(
            img => img.id === newPrimaryImage.id,
          );

          if (!imageAlreadyInCache) {
            cache.writeQuery({
              query: GET_USER_IMAGES,
              variables: { user_id: userId },
              data: {
                user_images: [
                  ...existingImages.map(image => ({
                    ...image,
                    is_primary: false,
                  })),
                  newPrimaryImage,
                ],
              },
            });
          }
        }

        updateCacheForPrimaryImage(cache, userId, newPrimaryImage);
      },
    });

    const [setPrimary] = useMutation(SET_PRIMARY_IMAGE, {
      update: (cache, { data }) => {
        if (!data?.update_user_images_by_pk) {
          return;
        }

        updateCacheForPrimaryImage(
          cache,
          userId,
          data.update_user_images_by_pk,
        );
      },
    });

    const handleClose = useCallback(() => {
      setError(null);
      setIsUploading(false);
      setShowGallery(false);
      setEnhancePanelOpen(false);
      onClose();
      setTimeout(() => setSelectedSource(null), 300);
    }, [onClose]);

    const handleGalleryClose = useCallback(() => {
      if (selectedSource) {
        setShowGallery(false);
      } else {
        handleClose();
      }
    }, [selectedSource, handleClose]);

    const createUserImageRecord = useCallback(
      async (
        imageUrl: string,
        setPrimary: boolean = true,
        aiEnhancedAt?: string,
      ): Promise<{ userImageId: string; galleryId: string }> => {
        const { data } = await insertUserImageAtomic({
          variables: {
            url: imageUrl,
            userId,
            setPrimary,
            aiEnhancedAt,
          },
        });

        if (!data?.insert_user_images_one) {
          throw new Error('Failed to create user image record');
        }

        return {
          userImageId: data.insert_user_images_one.id,
          galleryId: data.insert_user_images_one.image.id,
        };
      },
      [insertUserImageAtomic, userId],
    );

    const handleEditSave = useCallback(
      async (transform: ImageTransform) => {
        if (!selectedSource) {
          return;
        }

        setIsUploading(true);
        try {
          if (isCurrentImageUnchanged(selectedSource, transform)) {
            handleClose();
            return;
          }

          if (shouldOnlySetPrimary(selectedSource, transform)) {
            const userImageId = selectedSource.metadata?.userImageId;
            if (typeof userImageId !== 'string') {
              throw new Error('Missing userImageId for gallery image');
            }
            await setPrimary({
              variables: {
                userId,
                userImageId,
              },
            });
            handleClose();
            return;
          }

          const processedFile = await processImage(
            selectedSource,
            transform,
            t,
          );
          const uploadResult = await uploadFileAsync(uploadFile, processedFile);
          await createUserImageRecord(
            uploadResult.url,
            true,
            selectedSource.metadata?.aiEnhancedAt,
          );
          handleClose();
        } catch (error) {
          setError(error instanceof Error ? error.message : 'Upload failed');
        } finally {
          setIsUploading(false);
        }
      },
      [
        selectedSource,
        userId,
        setPrimary,
        handleClose,
        t,
        uploadFile,
        createUserImageRecord,
      ],
    );

    const handleGalleryImageSelect = useCallback(
      (image: ImageGalleryItem) => {
        if (!image.image.url) {
          setError(t('Selected image has no URL'));
          return;
        }
        setShowGallery(false);
        setSelectedSource({
          type: 'url',
          data: image.image.url,
          metadata: {
            galleryId: image.image.id,
            userImageId: image.id,
          },
        });
      },
      [t],
    );

    const handleFilesUploaded = useCallback(
      async (uploadedFiles: { id: string; url: string }[]) => {
        try {
          for (const file of uploadedFiles) {
            await insertUserImageAtomic({
              variables: { url: file.url, userId, setPrimary: false },
            });
          }
        } catch (error) {
          setError(
            error instanceof Error ? error.message : 'Failed to save images',
          );
        }
      },
      [insertUserImageAtomic, userId],
    );

    const handleSaveEnhancedImages = useCallback(
      async (base64Images: string[]) => {
        try {
          const file = await base64PngToJpegFile(base64Images[0]);
          const uploadResult = await uploadFileAsync(uploadFile, file);
          const aiEnhancedAt = new Date().toISOString();
          const { userImageId, galleryId } = await createUserImageRecord(
            uploadResult.url,
            false,
            aiEnhancedAt,
          );

          setSelectedSource({
            type: 'url',
            data: uploadResult.url,
            metadata: { galleryId, userImageId, aiEnhancedAt },
          });
          setEnhancePanelOpen(false);
        } catch (err) {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to save enhanced image',
          );
        }
      },
      [uploadFile, createUserImageRecord],
    );

    const currentUrl =
      selectedSource?.type === 'url' ? (selectedSource.data as string) : null;

    const enhanceImage = currentUrl
      ? { id: currentUrl, url: currentUrl }
      : null;

    return (
      <>
        {selectedSource && !showGallery && (
          <ImageUpload
            source={selectedSource}
            currentImageUrl={currentImageUrl}
            open={open}
            onClose={handleClose}
            onSave={handleEditSave}
            onChangePhoto={() => setShowGallery(true)}
            isLoading={isUploading}
            aspectRatio={1}
            cropShape="round"
            title={t('Edit profile picture')}
            onEnhanceClick={
              enhanceImage ? () => setEnhancePanelOpen(true) : undefined
            }
          />
        )}

        <ImageGalleryDialog
          open={open && showGallery}
          onClose={handleGalleryClose}
          entityId={userId}
          entityType="user"
          onSelectImage={handleGalleryImageSelect}
          onFilesUploaded={handleFilesUploaded}
        />

        <EnhanceImagePanel
          open={enhancePanelOpen}
          onClose={() => setEnhancePanelOpen(false)}
          image={enhanceImage}
          actions={actions}
          categories={categories}
          subcategories={subcategories}
          source="profile"
          maxImageDimension={1024}
          onEnhance={handleEnhance}
          onSaveImages={handleSaveEnhancedImages}
        />

        {error && <RASnackbar message={error} onClose={() => setError(null)} />}
      </>
    );
  },
);
